import { Box, Tab, Tabs } from "@mui/material";
import { useState } from "react";
import { PageContainer, PageHeader } from "../components/PageHeader";
import MenuComposer from "../components/distribution/MenuComposer";
import PremiseChannelsPage from "./PremiseChannelsPage";
import { tokens } from "../theme";

/**
 * Streaming TV — ONE menu item, not an accordion.
 *
 * Ruled by Sigge: *"let's merge channels, carousels and pages into one so that
 * streaming tv is just 1 menu item"*. It was three siblings in a collapsible
 * group, and the split did not match the job: the menu a guest meets is one
 * thing, and it was authored across two screens with the line-up on a third.
 *
 * ## Two sections, and they are genuinely two questions
 *
 * - **Menu** — the app's shape. Home and every tab, each with its rows, in the
 *   order a guest meets them.
 * - **Channels** — which channels this hotel carries and in what order. A
 *   different axis: it feeds every channel row rather than being one.
 *
 * They live on one screen because they are one product, and stay separate
 * sections because merging them further would put "what SVT1's rank is" beside
 * "which tab Films sits on", which are not decisions anybody makes together.
 */

type Section = "menu" | "channels";

export interface StreamingTvPageProps {
  premiseId: string;
}

export default function StreamingTvPage({ premiseId }: StreamingTvPageProps) {
  const [section, setSection] = useState<Section>("menu");

  return (
    <PageContainer>
      <PageHeader
        title="Streaming TV"
        subtitle={
          section === "menu"
            ? "The app as a guest meets it — every tab, and the rows behind it."
            : "The channels this hotel carries, and the order they tune in."
        }
      />

      <Box sx={{ borderBottom: `1px solid ${tokens.divider}`, mb: 3 }}>
        <Tabs value={section} onChange={(_, next: Section) => setSection(next)}>
          <Tab value="menu" label="Menu" />
          <Tab value="channels" label="Channels" />
        </Tabs>
      </Box>

      {/* Mounted by section rather than hidden with CSS: the channels page
          reads the whole catalogue, and paying for that while somebody
          rearranges a menu would be a cost with nothing on screen to show for
          it. */}
      {section === "menu" ? (
        <MenuComposer premiseId={premiseId} />
      ) : (
        <PremiseChannelsPage premiseId={premiseId} />
      )}
    </PageContainer>
  );
}
